import { createApp } from 'vue';
import { createPinia } from 'pinia';

import auth from '@lib/plugins/auth';
import /* { axiosInstance } from */ '@lib/plugins/axios';
import router from '@/router';

// Main layout component from ef lib
import '@lib/plugins/bootstrap'; // Force load of bootstap
import App from '../../lib/js/layouts/Base.vue';

// Additionals
import '@lib/../assets/fontawesome-pro-7.3.0-web/css/all.min.css';
import '@lib/../assets/fontawesome-pro-7.3.0-web/css/solid.min.css';
import '@lib/../assets/fontawesome-pro-7.3.0-web/css/brands.min.css';

// Create main vue app
const pinia = createPinia();
const app = createApp(App);

// Load main plugins
app.use(pinia);
app.use(router);
app.use(auth);

// Internationalization: the lib starts in the browser language and keeps the one picked in the profile menu
import I18n from '@lib/plugins/i18n';
import { merge } from 'lodash-es';

import lib_locale from '../../lib/js/locale';
import app_locales from '@/locale/index';

app.use(I18n, globalThis.env.app?.env == 'e2e' ? {} : merge(lib_locale, app_locales));

// PrimeVue component library, see https://primefaces.org/primevue/showcase/#/
import PrimeVue from 'primevue/config';
app.use(PrimeVue);

// Load additional components from the ef libraray, eg. the layouts sidebar
// We use the plugin system style to enshure that the components are loaded in the propper order
import EfLib from '@lib/eflib';
import type { ExtensionSlots } from '@lib/extensions/define';
app.use(EfLib, {
	plugins: {
		notification: true,
	},
	contributions: {
		slots: {
			'admin.settings': [
				{
					id: 'article_categories',
					icon: 'fa-tags',
					group: 'shop',
					component: () => import('@/pages/Admin/Settings/ArticleCategories.vue'),
				},
			],
			// The settings of a team: its address next to the lib's members and events
			'context.settings': [
				{
					id: 'general',
					icon: 'fa-gear',
					group: 'general',
					label: 'tabs.team_settings.general',
					contexts: ['team'],
					ability: 'write',
					order: 0,
					component: () => import('@/pages/Team/Settings/General.vue'),
					props: ({ record }: ExtensionSlots['context.settings']) => ({ team: record }),
				},
			],
		},
	},
});

import AppBroadcastPlugin from '@lib/plugins/broadcast';
app.use(AppBroadcastPlugin);

// Load additional handfy plugins
import Moment from '@lib/plugins/moment';
app.use(Moment);

// The sidebar is custom and is defined in the app
import Sidebar from '@/sidebar';
app.use(Sidebar);

// App Template Component for Notifications
import Notification from '@lib/plugins/layouts/notification';
app.use(Notification);

// The context works pretty much the same in all apps and is only customized in the app
import { AppApplicationPlugin } from '@lib/plugins/layouts/application';
app.use(AppApplicationPlugin);

// The context works pretty much the same in all apps and is only customized in the app
import { AppContextPlugin } from '@lib/plugins/layouts/context';
app.use(AppContextPlugin);

import Filters from '@lib/filters';
app.use(Filters);

// Enable performance tracking
if (import.meta.env.DEV) {
	app.config.performance = true;
}

// Mount the app
app.mount('#app');
